import { Component, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { IbercajaService } from '../services/ibercaja.service';

interface FormatOption {
  value: number;
  label: string;
  description: string;
}

@Component({
  selector: 'app-ibercaja-upload',
  imports: [CommonModule, FormsModule],
  templateUrl: './ibercaja-upload.html',
  styleUrl: './ibercaja-upload.css',
})
export class IbercajaUpload {
  csvFilePath = signal<string>('');
  errorMessage = signal<string>('');
  successMessage = signal<string>('');
  appraisalIds = signal<string[]>([]);
  isSending = signal<boolean>(false);

  email = '';
  includeCertificates = true;
  ignoreValidation = true;
  format = 0;

  readonly formatOptions: FormatOption[] = [
    {
      value: 0,
      label: 'FORMAT_DEFAULT',
      description: 'RecordId-Version-AppraisalId.pdf / RecordId_Version-AppraisalId_cer.pdf'
    },
    {
      value: 1,
      label: 'FORMAT_RECORD_EXTERNAL_CER',
      description: 'RecordId_ExternalRecordId.pdf / RecordId_ExternalRecordId_cer.pdf'
    },
    {
      value: 2,
      label: 'FORMAT_EXTERNAL_C',
      description: 'ExternalRecordId_t.pdf / ExternalRecordId_c.pdf'
    },
    {
      value: 3,
      label: 'FORMAT_UCI_EXTERNAL_CER',
      description: 'ExternalRecordId.pdf / ExternalRecordId_cer.pdf'
    },
    {
      value: 4,
      label: 'FORMAT_EXTERNAL_RECORD_CERTIFICADO',
      description: 'ExternalRecordId_RecordId_informe.pdf / ExternalRecordId_RecordId_certificado.pdf'
    }
  ];

  constructor(private router: Router, private ibercajaService: IbercajaService) {}

  goBack() {
    this.router.navigate(['/home']);
  }

  selectCsvFile() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '.csv');

    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        this.csvFilePath.set(file.name);
        this.processCsvFile(file);
      }
    };

    input.click();
  }

  private processCsvFile(file: File) {
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const content = e.target?.result as string;
        this.parseIds(content);
      } catch (error: any) {
        this.errorMessage.set(`Error al leer el archivo CSV: ${error.message || 'Error desconocido'}`);
        this.appraisalIds.set([]);
      }
    };

    reader.readAsText(file);
  }

  private parseIds(content: string) {
    this.errorMessage.set('');
    this.successMessage.set('');

    const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);

    if (lines.length === 0) {
      this.errorMessage.set('El archivo CSV está vacío');
      this.appraisalIds.set([]);
      return;
    }

    const ids: string[] = [];
    for (const line of lines) {
      const cells = line.split(';');
      for (const cell of cells) {
        const trimmed = cell.trim();
        if (trimmed.length > 0) {
          ids.push(trimmed);
        }
      }
    }

    if (ids.length === 0) {
      this.errorMessage.set('No se encontraron IDs en el archivo CSV');
      this.appraisalIds.set([]);
      return;
    }

    this.appraisalIds.set(ids);
  }

  async sendRequest() {
    if (this.appraisalIds().length === 0 || !this.email) {
      return;
    }

    this.isSending.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    try {
      await firstValueFrom(
        this.ibercajaService.generateTasaciones({
          appraisalIds: this.appraisalIds().join(';'),
          email: this.email,
          includeCertificates: this.includeCertificates,
          ignoreValidation: this.ignoreValidation,
          format: this.format
        })
      );
      this.successMessage.set(`Solicitud enviada correctamente para ${this.appraisalIds().length} tasaciones`);
    } catch (error: any) {
      const message = error.error?.message || error.message || 'Error desconocido al enviar la solicitud';
      this.errorMessage.set(`Error en el envío: ${message}`);
    } finally {
      this.isSending.set(false);
    }
  }

  downloadExampleCsv() {
    const exampleContent = `303821;303822;303823`;

    const blob = new Blob([exampleContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'ejemplo_ibercaja.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getFormatDescription(formatValue: number): string {
    const format = this.formatOptions.find(f => f.value === formatValue);
    return format ? format.description : '';
  }
}
